import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconDeleteTableQuestionBank,
  iconEditTableQuestionBank,
} from '../../assets';
import { useDebounce } from '../../hooks/useDebounce';
import type { Theme } from '../../theme';
import { useTheme } from '../../theme';
import { QuestionBankFilter } from './QuestionBankFilter';
import type {
  FetchQuestionsResponse,
  QuestionBankProps,
  QuestionListItem,
} from './types';

const DEBOUNCE_MS = 400;
const DEFAULT_PAGE_SIZE = 10;
const ACTION_COLUMN_WIDTH = 120;
const ACTION_ICON_SIZE = 36;

type TranslationItem = {
  languageCode?: string;
  title?: string;
  description?: string;
  name?: string;
  text?: string;
};

function isTranslationItem(v: unknown): v is TranslationItem {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) return false;
  return true;
}

function getTextFromTranslations(
  translations: unknown,
  language: string
): string {
  if (!Array.isArray(translations)) return '';
  const items = translations.filter(isTranslationItem);
  if (items.length === 0) return '';

  const preferred = language.toLowerCase().startsWith('es') ? 'es-ES' : 'en-US';
  const fallback = preferred === 'en-US' ? 'es-ES' : 'en-US';
  const pickText = (item?: TranslationItem) =>
    item?.title?.trim() ||
    item?.description?.trim() ||
    item?.name?.trim() ||
    item?.text?.trim() ||
    '';

  const preferredText = pickText(
    items.find(item => item.languageCode === preferred)
  );
  if (preferredText) return preferredText;

  const fallbackText = pickText(items.find(item => item.languageCode === fallback));
  if (fallbackText) return fallbackText;

  return items.map(pickText).find(Boolean) ?? '';
}

function getDifficultyBadgeStyle(
  difficulty: string | undefined,
  theme: Theme
): React.CSSProperties {
  const base = {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.shape.borderRadius.sm2,
    fontSize: theme.typography.fontSize.xs,
    fontWeight: theme.typography.fontWeight.medium,
  };
  // Backend pode retornar `difficultyLevel.name` já traduzido (ex.: "Baja"/"Media"/"Alta")
  // ou como 0/1/2 dependendo do tenant. Normalizamos para as chaves do front.
  const d = (difficulty ?? '').toString().trim().toLowerCase();
  const normalized =
    d === '0' || d === 'low' || d === 'baja' || d === 'easy'
      ? 'low'
      : d === '1' || d === 'medium' || d === 'media'
        ? 'medium'
        : d === '2' || d === 'high' || d === 'alta' || d === 'hard'
          ? 'high'
          : '';

  if (normalized === 'low')
    return {
      ...base,
      backgroundColor: theme.palette.status.background,
      border: `${theme.shape.borderWidth.thin} solid ${theme.palette.status.border}`,
      color: theme.palette.status.text,
    };
  if (normalized === 'medium')
    return {
      ...base,
      backgroundColor: theme.palette.roles.background,
      border: `${theme.shape.borderWidth.thin} solid ${theme.palette.roles.border}`,
      color: theme.palette.roles.text,
    };
  if (normalized === 'high') {
    const quiz = theme.palette.createCourse?.quizModal;
    return {
      ...base,
      backgroundColor: quiz?.finalExamBg ?? theme.palette.error.main,
      border: `${theme.shape.borderWidth.thin} solid ${quiz?.finalExamBorder ?? theme.palette.error.main}`,
      color: quiz?.finalExamText ?? theme.palette.error.main,
    };
  }
  return {
    ...base,
    backgroundColor: theme.palette.status.background,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.status.border}`,
    color: theme.palette.status.text,
  };
}

/** Retorna texto para exibição; use sentinel "None" para traduzir via i18n (questionBank.linked_courses_none). */
function formatLinkedCourses(value: string | string[] | undefined): string {
  if (value == null) return 'None';
  if (typeof value === 'string') return value.trim() || 'None';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ') || 'None';
  return 'None';
}

export function QuestionBank({
  fetchQuestions,
  difficultyOptions = [],
  categoryOptions = [],
  title,
  subtitle,
  searchPlaceholder,
  createButtonLabel,
  onCreateQuestion,
  onEdit,
  onDelete,
  searchDebounceMs = DEBOUNCE_MS,
}: QuestionBankProps) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const titleText = title ?? t('questionBank.title');
  const subtitleText = subtitle ?? t('questionBank.subtitle');
  const searchPlaceholderText =
    searchPlaceholder ?? t('questionBank.search_placeholder');
  const createLabelText = createButtonLabel ?? t('questionBank.add_question');

  const [keyword, setKeyword] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [category, setCategory] = useState('');
  const debouncedSearch = useDebounce(keyword, searchDebounceMs);

  const [data, setData] = useState<QuestionListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const res: FetchQuestionsResponse = await fetchQuestions({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedSearch.trim() || undefined,
        difficulty: difficulty || undefined,
        categoryId: category || undefined,
      });
      setData(res.data);
      setTotal(res.total);
    } catch {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    fetchQuestions,
    pagination.pageIndex,
    pagination.pageSize,
    debouncedSearch,
    difficulty,
    category,
  ]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize));

  const difficultyFilterOptions = useMemo(() => {
    const all = { value: '', label: t('questionBank.all_difficulties') };
    return [all, ...difficultyOptions];
  }, [t, difficultyOptions]);

  const categoryFilterOptions = useMemo(() => {
    const all = { value: '', label: t('questionBank.all_categories') };
    return [all, ...categoryOptions];
  }, [t, categoryOptions]);

  const columns = useMemo<ColumnDef<QuestionListItem>[]>(
    () => [
      {
        accessorKey: 'question',
        header: t('questionBank.column_question'),
        cell: ({ row }) => {
          const q =
            getTextFromTranslations(row.original.translations, i18n.language) ||
            (row.original.question ?? '');
          const tableLayout = theme.layout?.questionBankTable;
          return (
            <div
              style={{
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: tableLayout?.questionColumnMaxWidth ?? 280,
              }}
            >
              {q || t('questionBank.no_value')}
            </div>
          );
        },
      },
      {
        accessorKey: 'difficulty',
        header: t('questionBank.column_difficulty'),
        cell: ({ row }) => {
          const raw = String(row.original.difficulty ?? '').trim();
          const d = raw.toLowerCase();
          const normalized =
            d === '0' || d === 'low' || d === 'baja' || d === 'easy'
              ? 'low'
              : d === '1' || d === 'medium' || d === 'media'
                ? 'medium'
                : d === '2' || d === 'high' || d === 'alta' || d === 'hard'
                  ? 'high'
                  : '';

          const label =
            normalized === 'low'
              ? t('questionBank.create.difficulty_low')
              : normalized === 'medium'
                ? t('questionBank.create.difficulty_medium')
                : normalized === 'high'
                  ? t('questionBank.create.difficulty_high')
                  : raw || t('questionBank.no_value');
          return (
            <span style={getDifficultyBadgeStyle(raw || undefined, theme)}>
              {label}
            </span>
          );
        },
      },
      {
        accessorKey: 'category',
        header: t('questionBank.column_category'),
        cell: ({ row }) => (
          <span style={{ color: theme.palette.text.primary }}>
            {row.original.category ?? t('questionBank.no_value')}
          </span>
        ),
      },
      {
        accessorKey: 'linkedCourses',
        header: t('questionBank.column_linked_courses'),
        cell: ({ row }) => {
          const raw = formatLinkedCourses(row.original.linkedCourses);
          const tableLayout = theme.layout?.questionBankTable;
          return (
            <span
              style={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.fontSize.sm,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: tableLayout?.linkedCoursesMaxWidth ?? 200,
                display: 'inline-block',
              }}
            >
              {raw === 'None' ? t('questionBank.linked_courses_none') : raw}
            </span>
          );
        },
      },
      {
        accessorKey: 'usage',
        header: t('questionBank.column_usage'),
        cell: ({ row }) => (
          <span style={{ color: theme.palette.text.primary }}>
            {row.original.usage ?? 0}
          </span>
        ),
      },
      {
        id: 'actions',
        header: t('questionBank.column_actions'),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0,
                width: '100%',
              }}
            >
              {onEdit && (
                <button
                  type="button"
                  className="questionbank-btn-icon"
                  onClick={() => onEdit(item)}
                  aria-label={t('questionBank.edit_aria')}
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: theme.palette.text.primary,
                    padding: 0,
                  }}
                >
                  <img
                    src={iconEditTableQuestionBank}
                    alt=""
                    width={ACTION_ICON_SIZE}
                    height={ACTION_ICON_SIZE}
                  />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  className="questionbank-btn-icon"
                  onClick={() => onDelete(item)}
                  aria-label={t('questionBank.delete_aria')}
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: theme.palette.error.main,
                    padding: 0,
                  }}
                >
                  <img
                    src={iconDeleteTableQuestionBank}
                    alt=""
                    width={ACTION_ICON_SIZE}
                    height={ACTION_ICON_SIZE}
                  />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [theme, onEdit, onDelete, t, i18n.language]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount,
    state: { pagination },
    onPaginationChange: updater => {
      setPagination(prev =>
        typeof updater === 'function' ? updater(prev) : prev
      );
    },
  });

  const containerStyle: React.CSSProperties = {
    padding: theme.spacing.lg,
    fontFamily: theme.typography.fontFamily,
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: theme.spacing.xs,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize['2xl'],
    color: theme.palette.text.primary,
  };

  const subtitleStyle: React.CSSProperties = {
    marginBottom: theme.spacing.lg,
    fontSize: theme.typography.fontSize.base,
    color: theme.palette.text.secondary,
  };

  const tableWrapperStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.sm,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
    overflow: 'hidden',
    boxShadow:
      theme.layout?.questionBankTable?.tableCardShadow ??
      '0 1px 3px rgba(0,0,0,0.08)',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
  };

  const tableLayout = theme.layout?.questionBankTable;

  const getThWidth = (columnId: string): React.CSSProperties => {
    if (!tableLayout) return {};
    if (columnId === 'actions')
      return {
        width: ACTION_COLUMN_WIDTH,
        minWidth: ACTION_COLUMN_WIDTH,
        maxWidth: ACTION_COLUMN_WIDTH,
      };
    if (columnId === 'difficulty')
      return {
        width: tableLayout.difficultyColumnWidth,
        minWidth: tableLayout.difficultyColumnWidth,
      };
    if (columnId === 'usage')
      return {
        width: tableLayout.usageColumnWidth,
        minWidth: tableLayout.usageColumnWidth,
      };
    return {};
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: theme.spacing.md,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.default,
    borderBottom: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
  };

  const tdStyle: React.CSSProperties = {
    padding: theme.spacing.md,
    fontSize: theme.typography.fontSize.base,
    borderBottom: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
    overflow: 'hidden',
    verticalAlign: 'middle',
  };

  const paginationStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    flexWrap: 'wrap',
    gap: theme.spacing.md,
  };

  const buttonClickStyle = `
    .questionbank-btn-icon { transition: transform 0.12s ease, opacity 0.12s ease; }
    .questionbank-btn-icon:active { transform: scale(0.9); opacity: 0.8; }
    .questionbank-btn { transition: transform 0.12s ease, opacity 0.12s ease, background-color 0.12s ease; }
    .questionbank-btn:active { transform: scale(0.97); }
  `;

  return (
    <div style={containerStyle} data-e2e="question-bank-list">
      <style>{buttonClickStyle}</style>
      <h1 style={headerStyle}>{titleText}</h1>
      <p style={subtitleStyle}>{subtitleText}</p>

      <QuestionBankFilter
        searchValue={keyword}
        onSearchChange={setKeyword}
        searchPlaceholder={searchPlaceholderText}
        searchAriaLabel={t('questionBank.search_aria')}
        difficultyOptions={difficultyFilterOptions}
        difficultyValue={difficulty}
        onDifficultyChange={setDifficulty}
        categoryOptions={categoryFilterOptions}
        categoryValue={category}
        onCategoryChange={setCategory}
        onCreateQuestion={onCreateQuestion}
        addQuestionLabel={createLabelText}
        addQuestionAriaLabel={t('questionBank.add_question_aria')}
        dataE2eSearchInput="question-bank-search-input"
        dataE2eAddButton="question-bank-create-button"
      />

      <div style={tableWrapperStyle}>
        {loading ? (
          <div
            style={{
              padding: theme.spacing['2xl'],
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            {t('questionBank.loading')}
          </div>
        ) : (
          <>
            <table style={tableStyle}>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        style={{
                          ...thStyle,
                          ...getThWidth(header.column.id),
                          ...(header.column.id === 'actions'
                            ? { textAlign: 'center' }
                            : {}),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => {
                  const questionId =
                    (row.original as Partial<QuestionListItem>)?.id ??
                    undefined;
                  return (
                    <tr
                      key={row.id}
                      data-e2e={
                        questionId
                          ? `question-bank-row-${questionId}`
                          : 'question-bank-row'
                      }
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          style={{
                            ...tdStyle,
                            ...getThWidth(cell.column.id),
                            ...(cell.column.id === 'actions'
                              ? { textAlign: 'center' }
                              : {}),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={paginationStyle}>
              <span
                style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.palette.text.secondary,
                }}
              >
                {t('questionBank.pagination_summary', {
                  count: data.length,
                  total,
                })}
              </span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                }}
              >
                <button
                  type="button"
                  className="questionbank-btn"
                  disabled={!table.getCanPreviousPage()}
                  onClick={() => table.previousPage()}
                  style={{
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily,
                    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                    borderRadius: theme.shape.borderRadius.sm,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.secondary,
                    cursor: table.getCanPreviousPage()
                      ? 'pointer'
                      : 'not-allowed',
                    opacity: table.getCanPreviousPage() ? 1 : 0.6,
                  }}
                >
                  {t('questionBank.previous')}
                </button>
                {Array.from({ length: pageCount }, (_, i) => i + 1).map(
                  pageNum => {
                    const isActive = pagination.pageIndex + 1 === pageNum;
                    return (
                      <button
                        key={pageNum}
                        type="button"
                        className="questionbank-btn"
                        onClick={() => table.setPageIndex(pageNum - 1)}
                        style={{
                          minWidth: tableLayout?.paginationButtonMinWidth ?? 32,
                          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                          fontSize: theme.typography.fontSize.sm,
                          fontFamily: theme.typography.fontFamily,
                          border: isActive
                            ? 'none'
                            : `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                          borderRadius: theme.shape.borderRadius.sm,
                          backgroundColor: isActive
                            ? theme.palette.primary.main
                            : theme.palette.background.paper,
                          color: isActive
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.primary,
                          cursor: 'pointer',
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}
                <button
                  type="button"
                  className="questionbank-btn"
                  disabled={!table.getCanNextPage()}
                  onClick={() => table.nextPage()}
                  style={{
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily,
                    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                    borderRadius: theme.shape.borderRadius.sm,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    cursor: table.getCanNextPage() ? 'pointer' : 'not-allowed',
                    opacity: table.getCanNextPage() ? 1 : 0.6,
                  }}
                >
                  {t('questionBank.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
